import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, ExternalLink, Search, AlertTriangle, CheckCircle, XCircle, Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parseISO, isAfter, isBefore, differenceInDays, addMonths } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';

interface EOLCycle {
  cycle: string;
  releaseDate: string;
  eol: string | boolean;
  latest: string;
  link: string;
  lts: boolean;
  support: string | boolean;
}

const POPULAR_PRODUCTS = [
  "windows", "ubuntu", "debian", "centos", "fedora",
  "python", "nodejs", "php", "go", "linux",
  "postgresql", "mysql", "redis", "mongodb",
  "kubernetes", "nginx", "openvpn", "esxi", "zabbix"
];

export function EOLFeed() {
  const { language, t } = useLanguage();
  const { product } = useParams<{ product: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>(product || "");
  const [productData, setProductData] = useState<EOLCycle[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hideEOL, setHideEOL] = useState(false);

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/api/eol/all.json');
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(data);
        setLoadingProducts(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load product list');
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Update selected product when URL param changes
  useEffect(() => {
    if (product) {
      setSelectedProduct(product);
    } else {
      setSelectedProduct("");
      setProductData([]);
    }
  }, [product]);

  // Fetch product data when selected
  useEffect(() => {
    if (!selectedProduct) return;

    const fetchProductData = async () => {
      setLoadingData(true);
      setError(null);
      try {
        const response = await fetch(`/api/eol/${selectedProduct}.json`);
        if (!response.ok) throw new Error('Failed to fetch product data');
        const data = await response.json();
        setProductData(data);
      } catch (err) {
        console.error('Error fetching product data:', err);
        setError('Failed to load product data');
      } finally {
        setLoadingData(false);
      }
    };

    fetchProductData();
  }, [selectedProduct]);

  const handleProductSelect = (prod: string) => {
    navigate(`/eol/${prod}`);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'd MMM yyyy', {
        locale: language === 'pl' ? pl : enUS
      });
    } catch (e) {
      return dateString;
    }
  };

  const getLifecycleProgress = (releaseDate: string, eol: string | boolean) => {
    if (typeof eol !== 'string') return eol === false ? 0 : 100;

    const start = parseISO(releaseDate).getTime();
    const end = parseISO(eol).getTime();
    const now = new Date().getTime();

    if (now > end) return 100;
    if (now < start) return 0;

    const total = end - start;
    const elapsed = now - start;

    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const getStatusColor = (eol: string | boolean) => {
    if (eol === false) return "text-green-500";
    if (eol === true) return "text-red-500";

    const eolDate = parseISO(eol as string);
    if (isBefore(eolDate, new Date())) return "text-red-500";
    if (isBefore(eolDate, addMonths(new Date(), 6))) return "text-yellow-500";
    return "text-green-500";
  };

  const filteredData = hideEOL
    ? productData.filter(cycle => {
      if (cycle.eol === true) return false;
      if (typeof cycle.eol === 'string' && isBefore(parseISO(cycle.eol), new Date())) return false;
      return true;
    })
    : productData;

  const activeVersionsCount = productData.filter(cycle => {
    if (cycle.eol === true) return false;
    if (typeof cycle.eol === 'string' && isBefore(parseISO(cycle.eol), new Date())) return false;
    return true;
  }).length;

  const nextEOLDate = productData
    .filter(cycle => typeof cycle.eol === 'string' && isAfter(parseISO(cycle.eol), new Date()))
    .sort((a, b) => new Date(a.eol as string).getTime() - new Date(b.eol as string).getTime())[0]?.eol;

  return (
    <section className="py-20 bg-secondary/50 dark:bg-secondary/20 min-h-screen">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl text-foreground uppercase">
            End of Life
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t('eol.subtitle')}
          </p>
          <Separator className="mt-4 mb-8 mx-auto w-24 bg-primary/50" />
        </div>

        {/* Search Section */}
        <div className="max-w-3xl mx-auto mb-12 space-y-6">
          <Card className="p-6 glass-card border-primary/20 shadow-lg">
            <div className="flex flex-col gap-4">
              <label className="text-sm font-medium text-muted-foreground">
                Wybierz produkt do sprawdzenia:
              </label>
              <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openCombobox}
                    className="w-full justify-between h-14 text-lg px-4 bg-background/50"
                  >
                    {selectedProduct ? (
                      <span className="font-semibold capitalize">{selectedProduct}</span>
                    ) : (
                      <span className="text-muted-foreground">Szukaj produktu (np. python, ubuntu)...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Wpisz nazwę produktu..." />
                    <CommandList className="max-h-[300px]">
                      <CommandEmpty>Nie znaleziono produktu.</CommandEmpty>
                      <CommandGroup>
                        {products.map((product) => (
                          <CommandItem
                            key={product}
                            value={product}
                            onSelect={(currentValue) => {
                              handleProductSelect(currentValue);
                              setOpenCombobox(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedProduct === product ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {product}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Popular Products Chips */}
            <div className="mt-4">
              <p className="text-xs text-muted-foreground mb-2">Popularne:</p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_PRODUCTS.map(prod => (
                  <Badge
                    key={prod}
                    variant="outline"
                    className={cn(
                      "cursor-pointer hover:bg-primary/10 hover:text-primary transition-colors px-3 py-1",
                      selectedProduct === prod ? "bg-primary/20 border-primary text-primary" : "bg-background/50"
                    )}
                    onClick={() => handleProductSelect(prod)}
                  >
                    {prod}
                  </Badge>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Results Section */}
        {loadingData ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="text-center text-destructive py-12">
            {error}
          </div>
        ) : selectedProduct && productData.length > 0 ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">

            {/* Header & Stats */}
            <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
              <div>
                <h3 className="text-3xl font-black capitalize tracking-tight">{selectedProduct}</h3>
                <p className="text-muted-foreground">Cykl życia i wsparcie techniczne</p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex items-center space-x-2 bg-background/50 p-2 rounded-lg border">
                  <Switch id="hide-eol" checked={hideEOL} onCheckedChange={setHideEOL} />
                  <Label htmlFor="hide-eol" className="cursor-pointer text-sm">Ukryj zakończone (EOL)</Label>
                </div>
                <Button variant="destructive" size="sm" asChild className="animate-pulse hover:animate-none">
                  <a href={`/cve?search=${selectedProduct}`} target="_blank" rel="noopener noreferrer">
                    <AlertTriangle className="mr-2 h-4 w-4" />
                    Sprawdź podatności
                  </a>
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 glass-card flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10 text-blue-500">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aktywne Wersje</p>
                  <p className="text-2xl font-bold">{activeVersionsCount}</p>
                </div>
              </Card>

              <Card className="p-4 glass-card flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-500/10 text-purple-500">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Najnowsza Wersja</p>
                  <p className="text-2xl font-bold">{productData[0]?.latest || '-'}</p>
                </div>
              </Card>

              <Card className="p-4 glass-card flex items-center gap-4">
                <div className="p-3 rounded-full bg-yellow-500/10 text-yellow-500">
                  <Calendar className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Najbliższy EOL</p>
                  <p className="text-2xl font-bold">
                    {nextEOLDate ? formatDate(nextEOLDate as string) : 'Brak'}
                  </p>
                </div>
              </Card>
            </div>

            {/* Data Table */}
            <Card className="overflow-hidden border-border/50 shadow-xl bg-card/50 backdrop-blur-sm">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[120px]">Wersja</TableHead>
                      <TableHead className="w-[200px]">Status</TableHead>
                      <TableHead className="w-[200px]">Cykl Życia</TableHead>
                      <TableHead>Data Wydania</TableHead>
                      <TableHead>Koniec Wsparcia (EOL)</TableHead>
                      <TableHead className="text-right">Info</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((cycle) => {
                      const isEOL = cycle.eol === true || (typeof cycle.eol === 'string' && isBefore(parseISO(cycle.eol), new Date()));
                      const progress = getLifecycleProgress(cycle.releaseDate, cycle.eol);
                      const statusColor = getStatusColor(cycle.eol);

                      return (
                        <TableRow
                          key={cycle.cycle}
                          className={cn(
                            "hover:bg-muted/30 transition-colors",
                            isEOL ? "opacity-60 bg-red-500/5" : "bg-green-500/5"
                          )}
                        >
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-bold text-lg">{cycle.cycle}</span>
                              {cycle.lts && <Badge variant="secondary" className="w-fit text-[10px] h-4 px-1">LTS</Badge>}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className={cn("font-medium flex items-center gap-2", statusColor)}>
                              {isEOL ? (
                                <><XCircle className="w-4 h-4" /> Zakończone</>
                              ) : (
                                <><CheckCircle className="w-4 h-4" /> Wspierane</>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Najnowsza: {cycle.latest}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Start</span>
                                <span>Koniec</span>
                              </div>
                              <Progress value={progress} className={cn("h-2", isEOL ? "bg-red-100" : "bg-green-100")} indicatorClassName={isEOL ? "bg-red-500" : "bg-green-500"} />
                              <div className="text-xs text-center font-mono text-muted-foreground">
                                {Math.round(progress)}%
                              </div>
                            </div>
                          </TableCell>

                          <TableCell className="font-mono text-sm">
                            {formatDate(cycle.releaseDate)}
                          </TableCell>

                          <TableCell className="font-mono text-sm font-medium">
                            {typeof cycle.eol === 'boolean'
                              ? (cycle.eol ? 'Tak' : 'Nie')
                              : formatDate(cycle.eol)
                            }
                            {typeof cycle.eol === 'string' && !isEOL && (
                              <div className="text-xs text-yellow-600 mt-1">
                                {differenceInDays(parseISO(cycle.eol), new Date())} dni do końca
                              </div>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            {cycle.link && (
                              <Button variant="ghost" size="icon" asChild className="hover:bg-primary/10 hover:text-primary">
                                <a href={cycle.link} target="_blank" rel="noopener noreferrer" title="Release Notes">
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        ) : selectedProduct ? (
          <div className="text-center py-12 text-muted-foreground">
            Brak danych dla tego produktu.
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground/50">
            <Search className="h-16 w-16 mx-auto mb-4 opacity-20" />
            <p>Wybierz produkt powyżej, aby zobaczyć szczegóły cyklu życia.</p>
          </div>
        )}
      </div>
    </section>
  );
}
